import React from 'react';

// Custom Neo-Brutalist / Terminal Style Icons
const TerminalIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" shapeRendering="crispEdges" {...props}>
    <rect x="3" y="3" width="18" height="18" />
    <path d="M3 9h18M9 21V9" />
  </svg>
);

const MetricsIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" shapeRendering="crispEdges" {...props}>
    <path d="M3 21h18V5" />
    <path d="M3 17h4v-4h4V9h4" />
  </svg>
);

const BracesIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" shapeRendering="crispEdges" {...props}>
    <path d="M7 4H4v6l-2 2 2 2v6h3M17 4h3v6l2 2-2 2v6h-3" />
  </svg>
);

const RepoIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" shapeRendering="crispEdges" {...props}>
    <path d="M4 20h16V8h-8L10 6H4v14z" />
    <line x1="4" y1="12" x2="20" y2="12" />
  </svg>
);

const DocumentIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" shapeRendering="crispEdges" {...props}>
    <path d="M4 4h10l6 6v10H4V4z" />
    <path d="M14 4v6h6" />
    <path d="M8 14h8M8 18h5" />
  </svg>
);

const ScheduleIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" shapeRendering="crispEdges" {...props}>
    <rect x="3" y="3" width="18" height="18" />
    <path d="M12 7v5l4 2" />
  </svg>
);

export const FeaturesSection: React.FC = () => {
  const features = [
    {
      id: '01',
      icon: TerminalIcon,
      title: "CENTRALIZED DASHBOARD",
      description: "View all your open PRs across multiple repositories in one place. Track review status, merge times, and recent activity at a glance."
    },
    {
      id: '02',
      icon: MetricsIcon,
      title: "PRODUCTIVITY METRICS",
      description: "Gain insights into your team's velocity with metrics like Average Merge Time, PR Size, and Daily Activity stats."
    },
    {
      id: '03',
      icon: BracesIcon,
      title: "CUSTOM REVIEW RULES",
      description: "Define custom instructions (e.g., \"Always use TDD\" or \"Prefer functional patterns\") that Codii will enforce during reviews."
    },
    {
      id: '04',
      icon: RepoIcon,
      title: "REPOSITORY CONTROL",
      description: "Easily toggle reviews on/off for specific repositories. Configure scan frequency to suit your workflow."
    },
    {
      id: '05',
      icon: DocumentIcon,
      title: "SMART SUMMARIES",
      description: "Get high-level summaries of changes in every PR, helping reviewers understand context before diving into the code."
    },
    {
      id: '06',
      icon: ScheduleIcon,
      title: "FLEXIBLE SCHEDULING",
      description: "Choose when to run reviews: automatically on every commit, only when a PR is opened, or trigger them manually."
    }
  ];

  return (
    <div className="w-full">
      <div className="mb-16 max-w-3xl">
        <h2 className="text-3xl font-bold mb-6 font-mono tracking-tight uppercase">
          SYSTEM CAPABILITIES
        </h2>
        <p className="text-lg text-muted-foreground font-mono leading-relaxed border-l-2 border-neutral-800 pl-4">
          Codii provides a comprehensive control plane for engineering quality, managing PRs, tracking metrics, and encouraging best practices.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 border-t border-l border-neutral-800">
        {features.map((feature) => (
          <div
            key={feature.id}
            className="group border-r border-b border-neutral-800 p-8 hover:bg-neutral-50 dark:hover:bg-neutral-900/50 transition-colors"
          >
            <div className="flex justify-between items-start mb-6">
              <span className="font-mono text-xs font-bold text-muted-foreground group-hover:text-foreground transition-colors">
                {feature.id}
              </span>
              <feature.icon className="w-6 h-6 text-neutral-950 dark:text-neutral-100 group-hover:scale-110 transition-transform duration-300" />
            </div>

            <h3 className="font-mono font-bold text-sm mb-3 uppercase tracking-wide">
              {feature.title}
            </h3>

            <p className="text-muted-foreground text-sm font-mono leading-relaxed">
              {feature.description}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};
